/** Рейтинг с запятой и одним знаком: 4.5 → «4,5». Нет значения — пустая строка. */
export function formatRating(value: number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return value.toFixed(1).replace('.', ',')
}

/** Число отзывов со склонением: «1 отзыв», «3 отзыва», «11 отзывов». */
export function reviewsCountLabel(count: number): string {
  const mod10 = count % 10
  const mod100 = count % 100
  const word = mod10 === 1 && mod100 !== 11
    ? 'отзыв'
    : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'отзыва' : 'отзывов'
  return `${count} ${word}`
}

/** Доли оценок (от 1 до 5) в процентах, округлённо; при нуле отзывов — нули. */
export function distributionPercents(distribution: number[]): number[] {
  const total = distribution.reduce((sum, n) => sum + n, 0)
  if (total === 0) return distribution.map(() => 0)
  return distribution.map(n => Math.round((n / total) * 100))
}

/** Ключевые атрибуты купленного варианта одной строкой: «Размер: M, Цвет: Синий». */
export function keyAttributesLabel(attrs: { name: string, value: string }[]): string {
  return attrs.map(a => `${a.name}: ${a.value}`).join(', ')
}

/** Сколько из пяти звёзд закрасить: средняя оценка округляется, значения вне 0–5 обрезаются. */
export function filledStarsCount(value: number | null | undefined): number {
  if (value === null || value === undefined || Number.isNaN(value)) return 0
  return Math.min(5, Math.max(0, Math.round(value)))
}

/** Подпись звёзд для экранных чтецов: «Оценка 4,5 из 5»; без оценки — «Нет оценок». */
export function ratingAriaLabel(value: number | null | undefined): string {
  if (!value || value <= 0) return 'Нет оценок'
  return `Оценка ${formatRating(value)} из 5`
}

/**
 * Строки гистограммы оценок от 5 к 1: число отзывов и доля в процентах.
 * Недостающие элементы распределения считаются нулями.
 */
export function distributionRows(distribution: number[]): { star: number, count: number, percent: number }[] {
  const counts = [1, 2, 3, 4, 5].map(star => distribution[star - 1] ?? 0)
  const percents = distributionPercents(counts)
  return [5, 4, 3, 2, 1].map(star => ({ star, count: counts[star - 1]!, percent: percents[star - 1]! }))
}

/**
 * Заголовок сводки рейтинга: оценка, звёзды и подпись числа отзывов.
 * «Нет отзывов» решает `count`, а не `rating` (средняя может прийти и 0, и null):
 * пока отзывов нет — «—», пустые звёзды и «Пока нет отзывов» вместо «0,0» и «0 отзывов».
 */
export function summaryHeadline(summary: { rating?: number | null, count: number }): {
  rating: string
  stars: number | null
  countLabel: string
} {
  if (summary.count <= 0) return { rating: '—', stars: null, countLabel: 'Пока нет отзывов' }
  const hasRating = typeof summary.rating === 'number' && summary.rating > 0
  return {
    rating: hasRating ? formatRating(summary.rating) : '—',
    stars: hasRating ? summary.rating! : null,
    countLabel: reviewsCountLabel(summary.count),
  }
}
